import { Chess } from 'chess.js';

/**
 * Lichess-style Chess Game Tree (DAG / Tree structure)
 * Each node represents a board position resulting from a move.
 * 
 * Node Structure:
 * {
 *   id: string,
 *   parentId: string | null,
 *   move: { from, to, san, lan, piece, captured, promotion } | null,
 *   fen: string,
 *   ply: number,           // 0 = root, 1 = 1.e4, 2 = 1..e5, 3 = 2.Nf3, etc.
 *   children: string[],    // IDs of child nodes. children[0] is Mainline, children[1..N] are Variations
 *   comments: string[],    // Array of comment strings
 *   nag: string | null     // NAG symbol e.g. "!", "!!", "?", "??", "!?", "?!", "+=", "-+", "="
 * }
 */

export const INITIAL_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

export function createEmptyTree(startingFen = INITIAL_FEN) {
    const rootNode = {
        id: 'root',
        parentId: null,
        move: null,
        fen: startingFen,
        ply: 0,
        children: [],
        comments: [],
        nag: null
    };

    return {
        rootId: 'root',
        currentNodeId: 'root',
        nodes: {
            root: rootNode
        }
    };
}

/**
 * Ensures tree structure is valid and has root node
 */
export function normalizeTree(tree, defaultFen = INITIAL_FEN) {
    if (!tree || !tree.nodes || !tree.nodes.root) {
        return createEmptyTree(defaultFen);
    }
    const currentNodeId = (tree.currentNodeId && tree.nodes[tree.currentNodeId]) 
        ? tree.currentNodeId 
        : tree.rootId || 'root';

    return {
        rootId: tree.rootId || 'root',
        currentNodeId,
        nodes: tree.nodes
    };
}

export function cleanFenKey(rawFen) {
    if (!rawFen) return '';
    const parts = rawFen.trim().split(/\s+/);
    return parts.slice(0, 3).join(' ');
}

/**
 * Add a move from the current active node
 */
export function addMoveToTree(tree, moveObj, newFen, prevFen = null) {
    const norm = normalizeTree(tree);
    const nodes = { ...norm.nodes };
    
    let targetParentNode = nodes[norm.currentNodeId];

    // If prevFen is provided, verify if current node matches prevFen.
    // If not, find the node in the tree whose position matches prevFen!
    if (prevFen) {
        const targetFenKey = cleanFenKey(prevFen);
        const currFenKey = targetParentNode ? cleanFenKey(targetParentNode.fen) : '';

        if (!targetParentNode || currFenKey !== targetFenKey) {
            // Find a node matching prevFen
            const matchingNodeId = Object.keys(nodes).find(id => {
                return cleanFenKey(nodes[id].fen) === targetFenKey;
            });

            if (matchingNodeId && nodes[matchingNodeId]) {
                targetParentNode = nodes[matchingNodeId];
            }
        }
    }

    if (!targetParentNode) targetParentNode = nodes[norm.rootId] || nodes['root'];
    if (!targetParentNode) return norm;

    // Check if move already exists as a child of targetParentNode
    for (const childId of targetParentNode.children) {
        const child = nodes[childId];
        if (child && child.move && (child.move.san === moveObj.san || child.move.lan === moveObj.lan)) {
            // Already exists, just move pointer to it
            return {
                ...norm,
                currentNodeId: childId,
                nodes
            };
        }
    }

    // Create new node
    const newNodeId = `node_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
    const newNode = {
        id: newNodeId,
        parentId: targetParentNode.id,
        move: {
            from: moveObj.from,
            to: moveObj.to,
            san: moveObj.san,
            lan: `${moveObj.from}${moveObj.to}${moveObj.promotion || ''}`,
            piece: moveObj.piece,
            captured: moveObj.captured || null,
            promotion: moveObj.promotion || null
        },
        fen: newFen,
        ply: targetParentNode.ply + 1,
        children: [],
        comments: [],
        nag: null
    };

    // Update parent's children array
    const updatedParentNode = {
        ...targetParentNode,
        children: [...targetParentNode.children, newNodeId]
    };

    nodes[targetParentNode.id] = updatedParentNode;
    nodes[newNodeId] = newNode;

    return {
        ...norm,
        currentNodeId: newNodeId,
        nodes
    };
}

/**
 * Jump to a specific node by ID
 */
export function setCurrentNodeId(tree, nodeId) {
    const norm = normalizeTree(tree);
    if (!norm.nodes[nodeId]) return norm;
    return {
        ...norm,
        currentNodeId: nodeId
    };
}

/**
 * Step back to parent
 */
export function goPreviousNode(tree) {
    const norm = normalizeTree(tree);
    const curr = norm.nodes[norm.currentNodeId];
    if (curr && curr.parentId && norm.nodes[curr.parentId]) {
        return {
            ...norm,
            currentNodeId: curr.parentId
        };
    }
    return norm;
}

/**
 * Step forward to child (default children[0])
 */
export function goNextNode(tree, childIdx = 0) {
    const norm = normalizeTree(tree);
    const curr = norm.nodes[norm.currentNodeId];
    if (curr && curr.children && curr.children[childIdx] && norm.nodes[curr.children[childIdx]]) {
        return {
            ...norm,
            currentNodeId: curr.children[childIdx]
        };
    }
    return norm;
}

/**
 * Jump to Root
 */
export function goToRootNode(tree) {
    const norm = normalizeTree(tree);
    return {
        ...norm,
        currentNodeId: norm.rootId
    };
}

/**
 * Jump to End of current mainline branch
 */
export function goToEndNode(tree) {
    const norm = normalizeTree(tree);
    let currId = norm.currentNodeId;
    while (norm.nodes[currId] && norm.nodes[currId].children.length > 0) {
        currId = norm.nodes[currId].children[0];
    }
    return {
        ...norm,
        currentNodeId: currId
    };
}

/**
 * Promote variation node to Mainline (swap child index with 0)
 */
export function promoteVariationNode(tree, nodeId) {
    const norm = normalizeTree(tree);
    const node = norm.nodes[nodeId];
    if (!node || !node.parentId) return norm;

    const parent = norm.nodes[node.parentId];
    if (!parent) return norm;

    const childIdx = parent.children.indexOf(nodeId);
    if (childIdx <= 0) return norm; // Already mainline

    // Reorder parent children so nodeId is at index 0
    const newChildren = [...parent.children];
    const [promotedId] = newChildren.splice(childIdx, 1);
    newChildren.unshift(promotedId);

    const updatedNodes = {
        ...norm.nodes,
        [parent.id]: {
            ...parent,
            children: newChildren
        }
    };

    return {
        ...norm,
        nodes: updatedNodes
    };
}

/**
 * Delete a node and all its subtrees
 */
export function deleteNodeAndSubtree(tree, nodeId) {
    const norm = normalizeTree(tree);
    if (nodeId === norm.rootId || !norm.nodes[nodeId]) return norm;

    const node = norm.nodes[nodeId];
    const parentId = node.parentId;
    const nodes = { ...norm.nodes };

    // Collect all descendant IDs
    const toDelete = new Set();
    const collect = (id) => {
        toDelete.add(id);
        const n = nodes[id];
        if (n && n.children) {
            n.children.forEach(collect);
        }
    };
    collect(nodeId);

    // Delete nodes
    toDelete.forEach(id => delete nodes[id]);

    // Remove from parent's children
    if (parentId && nodes[parentId]) {
        nodes[parentId] = {
            ...nodes[parentId],
            children: nodes[parentId].children.filter(cId => cId !== nodeId)
        };
    }

    // If active node was deleted, fallback to parent or root
    let newCurrentId = norm.currentNodeId;
    if (toDelete.has(norm.currentNodeId)) {
        newCurrentId = (parentId && nodes[parentId]) ? parentId : norm.rootId;
    }

    return {
        ...norm,
        currentNodeId: newCurrentId,
        nodes
    };
}

/**
 * Add / Update comment on node
 */
export function setNodeComment(tree, nodeId, commentText) {
    const norm = normalizeTree(tree);
    const node = norm.nodes[nodeId];
    if (!node) return norm;

    const trimmed = (commentText || '').trim();
    const newComments = trimmed ? [trimmed] : [];

    return {
        ...norm,
        nodes: {
            ...norm.nodes,
            [nodeId]: {
                ...node,
                comments: newComments
            }
        }
    };
}

/**
 * Set NAG symbol on node
 */
export function setNodeNag(tree, nodeId, nag) {
    const norm = normalizeTree(tree);
    const node = norm.nodes[nodeId];
    if (!node) return norm;

    return {
        ...norm,
        nodes: {
            ...norm.nodes,
            [nodeId]: {
                ...node,
                nag: nag || null
            }
        }
    };
}

/**
 * Get active path array of node IDs from Root -> CurrentNode
 */
export function getActivePathNodes(tree) {
    const norm = normalizeTree(tree);
    const path = [];
    let currId = norm.currentNodeId;

    while (currId && norm.nodes[currId]) {
        path.unshift(norm.nodes[currId]);
        currId = norm.nodes[currId].parentId;
    }
    return path;
}

/**
 * Convert legacy moveHistory + variations arrays into a ChessGameTree
 */
export function convertLegacyToTree(moveHistory = [], variations = [], moveComments = {}, startingFen = INITIAL_FEN) {
    let tree = createEmptyTree(startingFen);
    const tempChess = new Chess(startingFen);

    // Build Mainline from moveHistory
    moveHistory.forEach(item => {
        const san = typeof item === 'object' ? item.san : String(item || '');
        if (!san) return;
        try {
            const move = tempChess.move(san);
            if (move) {
                tree = addMoveToTree(tree, move, tempChess.fen());
                if (typeof item === 'object' && item.nag) {
                    tree = setNodeNag(tree, tree.currentNodeId, item.nag);
                }
            }
        } catch (e) {}
    });

    // Attach comments from moveComments map (key = move index in main line)
    if (moveComments && Object.keys(moveComments).length > 0) {
        const path = getActivePathNodes(tree); // root at 0, 1st move at 1
        Object.keys(moveComments).forEach(moveIdx => {
            const idx = parseInt(moveIdx, 10);
            const targetNode = path[idx + 1]; // +1 because root is 0
            if (targetNode) {
                tree = setNodeComment(tree, targetNode.id, moveComments[moveIdx]);
            }
        });
    }

    // Build variations
    variations.forEach(v => {
        if (!v || !v.moves || v.moves.length === 0) return;
        const branchPly = v.moveIndex !== undefined ? v.moveIndex : 0;
        
        // Find node at branchPly
        let branchId = 'root';
        const path = getActivePathNodes(tree);
        if (path[branchPly]) {
            branchId = path[branchPly].id;
        }

        // Move active pointer to branch node
        tree = setCurrentNodeId(tree, branchId);

        // Reconstruct position at branch node
        const varChess = new Chess(tree.nodes[branchId].fen);
        v.moves.forEach(sanStr => {
            const san = typeof sanStr === 'object' ? sanStr.san : String(sanStr || '');
            if (!san) return;
            try {
                const move = varChess.move(san);
                if (move) {
                    tree = addMoveToTree(tree, move, varChess.fen());
                }
            } catch (e) {}
        });
    });

    // Reset current node to root or end of mainline
    tree = goToRootNode(tree);
    return tree;
}

/**
 * Export entire GameTree to PGN format string
 */
export function exportTreeToPgn(tree, headers = {}) {
    const norm = normalizeTree(tree);
    
    let pgnStr = '';
    const dateStr = new Date().toISOString().split('T')[0].replace(/-/g, '.');

    pgnStr += `[Event "${headers.event || 'Classroom Analysis'}"]\n`;
    pgnStr += `[Site "${headers.site || 'Hayaa Chess Academy'}"]\n`;
    pgnStr += `[Date "${headers.date || dateStr}"]\n`;
    pgnStr += `[White "${headers.white || 'Coach'}"]\n`;
    pgnStr += `[Black "${headers.black || 'Students'}"]\n`;
    pgnStr += `[Result "${headers.result || '*'}"]\n`;
    if (norm.nodes.root.fen !== INITIAL_FEN) {
        pgnStr += `[SetUp "1"]\n`;
        pgnStr += `[FEN "${norm.nodes.root.fen}"]\n`;
    }
    pgnStr += `\n`;

    function buildPgnRecursive(nodeId, isMain = true) {
        const node = norm.nodes[nodeId];
        if (!node || node.children.length === 0) return '';

        let out = '';
        const mainChildId = node.children[0];
        const mainChild = norm.nodes[mainChildId];

        if (!mainChild || !mainChild.move) return out;

        const moveNum = Math.floor(mainChild.ply / 2) + (mainChild.ply % 2 === 1 ? 1 : 0);
        const isWhite = mainChild.ply % 2 === 1;

        // Move number formatting
        if (isWhite) {
            out += `${moveNum}. ${mainChild.move.san}`;
        } else {
            out += `${mainChild.move.san}`;
        }

        // NAG
        if (mainChild.nag) {
            out += `${mainChild.nag}`;
        }

        // Comments
        if (mainChild.comments && mainChild.comments.length > 0) {
            out += ` { ${mainChild.comments.join(' ')} }`;
        }

        out += ' ';

        // Side variations (children[1..N])
        for (let i = 1; i < node.children.length; i++) {
            const varChildId = node.children[i];
            const varChild = norm.nodes[varChildId];
            if (varChild && varChild.move) {
                const varMoveNum = Math.floor(varChild.ply / 2) + (varChild.ply % 2 === 1 ? 1 : 0);
                const varIsWhite = varChild.ply % 2 === 1;
                const varPrefix = varIsWhite ? `${varMoveNum}. ` : `${varMoveNum}... `;

                out += `( ${varPrefix}${varChild.move.san}`;
                if (varChild.nag) out += `${varChild.nag}`;
                if (varChild.comments && varChild.comments.length > 0) {
                    out += ` { ${varChild.comments.join(' ')} }`;
                }
                out += ' ' + buildPgnRecursive(varChildId, false) + ') ';
            }
        }

        // Continue mainline
        out += buildPgnRecursive(mainChildId, isMain);
        return out;
    }

    pgnStr += buildPgnRecursive(norm.rootId, true).trim();
    return pgnStr;
}
